import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WithdrawTicketModalComponent, WithdrawTicketResult } from '../../../shared/components/withdraw-ticket-modal/withdraw-ticket-modal.component';
import { ApiError } from '../../../abstract/api-error';
import { SupportTicketService } from '../../../core/data-services/support-ticket.service';
import { OrganizationService } from '../../../core/data-services/organization.service';
import { WebSocketService } from '../../../core/services/websocket.service';
import { ToastService } from '../../../core/services/toast.service';
import { SupportTicket } from '../../../core/data-models/support-ticket.model';
import { SupportTicketStatus } from '../../../core/data-models/support-ticket-status.model';
import { SupportTicketPriority } from '../../../core/data-models/support-ticket-priority.model';
import { SupportTicketCategory } from '../../../core/data-models/support-ticket-category.model';
import { SupportTicketStatusCounts } from '../../../core/response-models/support-ticket-status-counts.model';
import { SUPPORT_TICKET_CATEGORIES } from '../../../shared/constants/support-ticket-options';
import { environment } from '../../../../environments/environment';
import { formatPhoneNumber } from '../../../shared/utils/format.utils';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

interface FilterState {
  status: string;
  priority: string;
  category: string;
  dateFrom: string;
  dateTo: string;
  search: string;
}

@Component({
  selector: 'app-support-tickets',
  standalone: true,
  imports: [CommonModule, FormsModule, WithdrawTicketModalComponent, MaxLengthDirective],
  templateUrl: './support-tickets.component.html',
  styleUrls: ['./support-tickets.component.scss']
})
export class SupportTicketsComponent implements OnInit, OnDestroy {
  @ViewChild('withdrawModal') withdrawModal!: WithdrawTicketModalComponent;
  private destroy$ = new Subject<void>();

  isLoading = false;
  tickets: SupportTicket[] = [];
  filteredTickets: SupportTicket[] = [];

  // Technical support phone
  technicalSupportPhone = environment.technicalSupportPhone;
  formattedSupportPhone = formatPhoneNumber(environment.technicalSupportPhone);

  // Organization context
  organizationId: string | null = null;

  // Status counts from API
  statusCounts: SupportTicketStatusCounts = {
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0
  };

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalTickets = 0;
  totalPages = 0;

  // Filter state
  isFilterOpen = false;
  filters: FilterState = {
    status: '',
    priority: '',
    category: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  };
  activeFilterCount = 0;

  // For withdraw confirmation
  ticketToWithdraw: SupportTicket | null = null;

  // Category options from model
  categories = SUPPORT_TICKET_CATEGORIES.map(c => c.value);

  constructor(
    private router: Router,
    private ticketService: SupportTicketService,
    private organizationService: OrganizationService,
    private websocketService: WebSocketService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initialize();
    this.subscribeToWebSocketEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      // Get current organization
      const org = await this.organizationService.getMyOrganization();
      if (!org) {
        console.error('No organization found');
        this.isLoading = false;
        return;
      }
      this.organizationId = org.id;

      // Load tickets and counts in parallel
      await Promise.all([
        this.loadTickets(),
        this.loadStatusCounts()
      ]);
    } catch (error) {
      console.error('Error initializing support tickets:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Subscribe to real-time ticket events via WebSocket.
   * Refreshes the ticket list and shows a toast when a new ticket arrives or status changes.
   */
  private subscribeToWebSocketEvents(): void {
    // New ticket created - refresh list and show toast
    this.websocketService.on<{
      ticketId: string;
      ticketNumber: string;
      subject: string;
      status: string;
      priority: string;
      timestamp: string;
    }>('ticket.created').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[SupportTickets] New ticket created:', event);
      this.refreshList();
      this.toastService.info(`New ticket: ${event.subject || 'Support request'}`, {
        title: 'New Support Ticket',
        duration: 6000,
        icon: 'bi-ticket-detailed',
        action: {
          label: 'View',
          callback: () => this.router.navigate(['/maintenance/support-tickets', event.ticketId])
        }
      });
    });

    // Ticket updated (status, priority, assignment changes) - refresh list
    this.websocketService.on<{
      ticketId: string;
      eventType: string;
      timestamp: string;
    }>('ticket.updated').pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.refreshList();
    });

    // Ticket status changed - refresh list
    this.websocketService.on<{
      ticketId: string;
      oldStatus: string;
      newStatus: string;
      timestamp: string;
    }>('ticket.statusChanged').pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.refreshList();
    });
  }

  /**
   * Refresh tickets and status counts without showing loading spinner
   */
  private async refreshList(): Promise<void> {
    await Promise.all([
      this.loadTickets(),
      this.loadStatusCounts()
    ]);
  }

  async loadTickets(): Promise<void> {
    if (!this.organizationId) return;

    try {
      const response = await this.ticketService.getOrganizationTickets(
        this.organizationId,
        {
          status: this.filters.status as SupportTicketStatus || undefined,
          priority: this.filters.priority as SupportTicketPriority || undefined,
          category: this.filters.category as SupportTicketCategory || undefined,
          search: this.filters.search || undefined,
          page: this.currentPage,
          limit: this.pageSize
        }
      );

      this.tickets = response.items;
      this.filteredTickets = response.items;
      this.totalTickets = response.paging.totalItems;
      this.totalPages = response.paging.totalPages;
    } catch (error) {
      console.error('Error loading tickets:', error);
      this.tickets = [];
      this.filteredTickets = [];
    }
  }

  async loadStatusCounts(): Promise<void> {
    if (!this.organizationId) return;

    try {
      this.statusCounts = await this.ticketService.getOrganizationTicketCounts(this.organizationId);
    } catch (error) {
      console.error('Error loading status counts:', error);
    }
  }

  // Filter methods
  toggleFilter(): void {
    this.isFilterOpen = !this.isFilterOpen;
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when filters change
    this.updateActiveFilterCount();
    this.loadTickets();
  }

  updateActiveFilterCount(): void {
    let count = 0;
    if (this.filters.status) count++;
    if (this.filters.priority) count++;
    if (this.filters.category) count++;
    if (this.filters.dateFrom || this.filters.dateTo) count++;
    if (this.filters.search) count++;
    this.activeFilterCount = count;
  }

  clearFilters(): void {
    this.filters = {
      status: '',
      priority: '',
      category: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    };
    this.applyFilters();
  }

  // Navigation methods - now use dedicated pages
  createTicket(): void {
    this.router.navigate(['/maintenance/support-tickets/new']);
  }

  viewTicket(ticket: SupportTicket): void {
    this.router.navigate(['/maintenance/support-tickets', ticket.id]);
  }

  withdrawTicket(ticket: SupportTicket): void {
    this.ticketToWithdraw = ticket;
    this.withdrawModal.open(ticket.ticketNumber || ticket.id);
  }

  async onWithdrawConfirmed(result: WithdrawTicketResult): Promise<void> {
    if (this.ticketToWithdraw) {
      this.withdrawModal.setProcessing(true);
      try {
        await this.ticketService.deleteTicket(
          this.ticketToWithdraw.id,
          result.reason,
          result.details
        );
        // Reload tickets and counts after withdrawal
        await Promise.all([
          this.loadTickets(),
          this.loadStatusCounts()
        ]);
      } catch (error: unknown) {
        alert(ApiError.fromHttpError(error).errors[0] ?? 'Failed to withdraw ticket. Only open tickets can be withdrawn.');
      } finally {
        this.withdrawModal.setProcessing(false);
        this.ticketToWithdraw = null;
      }
    }
  }

  onWithdrawCancelled(): void {
    this.ticketToWithdraw = null;
  }

  // Helper methods
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'open':
        return 'bg-primary';
      case 'in_progress':
        return 'bg-warning text-dark';
      case 'resolved':
        return 'bg-success';
      case 'closed':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  }

  getPriorityBadgeClass(priority: string): string {
    switch (priority) {
      case 'urgent':
        return 'bg-danger';
      case 'high':
        return 'bg-warning text-dark';
      case 'medium':
        return 'bg-info text-dark';
      case 'low':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  getPriorityText(priority: string): string {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  }

  formatDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // Stats getters using API counts
  get openCount(): number {
    return this.statusCounts.open;
  }

  get inProgressCount(): number {
    return this.statusCounts.in_progress;
  }

  get resolvedCount(): number {
    return this.statusCounts.resolved + this.statusCounts.closed;
  }

  // Unique values for filters
  get uniqueCategories(): string[] {
    return this.categories;
  }

  // Pagination methods
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTickets();
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }
}
